import { createApi } from '@reduxjs/toolkit/query/react';
import { RestClientWrapper } from '@/core/api/restClientWrapper';
import { NotificationStore } from '@wunder/ui-components';
import { t } from '@lingui/core/macro';
import { SubscriptionCacheTags } from '../enums';
import {
  Subscription,
  SubscriptionListResponse,
  BuySubscriptionArgs,
  CancelSubscriptionArgs,
  ReactivateSubscriptionArgs,
  RenewSubscriptionArgs,
} from '../types/Subscription.types';

export const SubscriptionService = createApi({
  reducerPath: 'api-subscriptions',
  baseQuery: RestClientWrapper,
  tagTypes: [SubscriptionCacheTags.SUBSCRIPTIONS, SubscriptionCacheTags.SUBSCRIPTION_DETAIL],
  endpoints: (builder) => ({
    // Get available subscriptions for a branch (catalog)
    requestAvailableSubscriptions: builder.query<SubscriptionListResponse, { branchId: number }>({
      query: ({ branchId }) => ({
        url: '/front/subscriptions',
        requestParams: {
          params: { branchId },
        },
      }),
      providesTags: [SubscriptionCacheTags.SUBSCRIPTIONS],
    }),

    // Get user's owned subscriptions
    requestMySubscriptions: builder.query<SubscriptionListResponse, void>({
      query: () => ({
        url: '/front/subscriptions',
        // No params - this endpoint returns user's subscriptions
      }),
      providesTags: [SubscriptionCacheTags.SUBSCRIPTIONS],
      transformResponse: (response: SubscriptionListResponse): SubscriptionListResponse => {
        // The API returns an array of objects with nested structure:
        // [{ subscription: {...}, boughtAt: "...", validUntil: "...", autoRenewal: "..." }]
        // We need to flatten it to match the Subscription type
        if (!Array.isArray(response)) {
          return [];
        }
        return response.map((item: any) => {
          // Merge subscription data with user-specific fields
          // Explicitly preserve details if it exists
          return {
            ...item.subscription,
            details: item.subscription?.details,
            boughtAt: item.boughtAt,
            validUntil: item.validUntil,
            autoRenewal: item.autoRenewal,
            nextBillingDate: item.nextBillingDate,
            paymentMethod: item.paymentMethod,
            canChangePaymentMethod: item.canChangePaymentMethod,
          };
        });
      },
    }),

    requestSubscriptionDetails: builder.query<Subscription, string>({
      query: (subscriptionCode) => ({
        url: `/front/subscriptions/${subscriptionCode}`,
      }),
      providesTags: (result, error, subscriptionCode) => [
        { type: SubscriptionCacheTags.SUBSCRIPTION_DETAIL, id: subscriptionCode },
      ],
    }),

    requestBuySubscription: builder.mutation<void, BuySubscriptionArgs>({
      query: ({ subscriptionCode }) => ({
        url: `/front/subscriptions/buy/${subscriptionCode}`,
        requestParams: { method: 'post' },
        config: {
          onRequestSuccess: () => {
            NotificationStore.addNotification({
              type: 'success',
              title: t({ id: 'notifications.general.success', message: 'Success' }),
              content: t({
                id: 'subscriptions.purchase.success',
                message: 'Plan is now active.',
              }),
              duration: 5000,
              position: 'bottom-right',
            });
          },
        },
      }),
      invalidatesTags: [
        SubscriptionCacheTags.SUBSCRIPTIONS,
        SubscriptionCacheTags.SUBSCRIPTION_DETAIL,
      ],
    }),

    requestCancelSubscription: builder.mutation<{ validUntil: string }, CancelSubscriptionArgs>({
      query: ({ subscriptionCode }) => ({
        url: `/front/subscriptions/cancel/${subscriptionCode}`,
        requestParams: { method: 'post' },
        config: {
          onRequestSuccess: () => {
            NotificationStore.addNotification({
              type: 'info',
              title: t({
                id: 'subscriptions.cancel.title',
                message: 'Subscription Updated',
              }),
              content: t({
                id: 'subscriptions.cancel.success',
                message: 'Cancellation scheduled.',
              }),
              duration: 5000,
              position: 'bottom-right',
            });
          },
        },
      }),
      invalidatesTags: [
        SubscriptionCacheTags.SUBSCRIPTIONS,
        SubscriptionCacheTags.SUBSCRIPTION_DETAIL,
      ],
    }),

    requestReactivateSubscription: builder.mutation<void, ReactivateSubscriptionArgs>({
      query: ({ subscriptionCode }) => ({
        url: `/front/subscriptions/reactivate/${subscriptionCode}`,
        requestParams: { method: 'post' },
        config: {
          onRequestSuccess: () => {
            NotificationStore.addNotification({
              type: 'success',
              title: t({ id: 'notifications.general.success', message: 'Success' }),
              content: t({
                id: 'subscriptions.reactivate.success',
                message: 'Subscription successfully reactivated.',
              }),
              duration: 5000,
              position: 'bottom-right',
            });
          },
        },
      }),
      invalidatesTags: [
        SubscriptionCacheTags.SUBSCRIPTIONS,
        SubscriptionCacheTags.SUBSCRIPTION_DETAIL,
      ],
    }),

    requestRenewSubscription: builder.mutation<void, RenewSubscriptionArgs>({
      query: ({ subscriptionCode }) => ({
        url: `/front/subscriptions/renew/${subscriptionCode}`,
        requestParams: { method: 'post' },
        config: {
          onRequestSuccess: () => {
            NotificationStore.addNotification({
              type: 'success',
              title: t({ id: 'notifications.general.success', message: 'Success' }),
              content: t({
                id: 'subscriptions.renew.success',
                message: 'Subscription renewed successfully.',
              }),
              duration: 5000,
              position: 'bottom-right',
            });
          },
        },
      }),
      invalidatesTags: [
        SubscriptionCacheTags.SUBSCRIPTIONS,
        SubscriptionCacheTags.SUBSCRIPTION_DETAIL,
      ],
    }),
  }),
});

export const {
  useRequestAvailableSubscriptionsQuery,
  useRequestMySubscriptionsQuery,
  useRequestSubscriptionDetailsQuery,
  useRequestBuySubscriptionMutation,
  useRequestCancelSubscriptionMutation,
  useRequestReactivateSubscriptionMutation,
  useRequestRenewSubscriptionMutation,
} = SubscriptionService;
